/**
 * Disclosure controller for navigation panels emitted by the Dropdown walker.
 *
 * Toggles `aria-expanded` on the trigger and `inert` on the panel.
 *
 * Imported for its side effects (no exports): it wires up every disclosure trigger, or no-ops if none is present.
 */

function setExpanded(trigger: HTMLElement, panel: HTMLElement, expanded: boolean): void {
	trigger.setAttribute('aria-expanded', String(expanded));
	if (expanded) {
		panel.removeAttribute('inert');
	} else {
		panel.setAttribute('inert', '');
	}
}

function initNavDisclosures(): void {
	const hoverCapable = window.matchMedia('(hover: hover)');

	document.querySelectorAll<HTMLElement>('[data-nav-disclosure]').forEach((trigger) => {
		const panelId = trigger.getAttribute('aria-controls');
		const panel = panelId ? document.getElementById(panelId) : null;
		const root = trigger.closest('li');

		if (!panel || !root) {
			return;
		}

		const isOpen = (): boolean => trigger.getAttribute('aria-expanded') === 'true';

		// 1. Click toggles the panel.
		trigger.addEventListener('click', () => setExpanded(trigger, panel, !isOpen()));

		// 2. Hover opens/closes on hover-capable pointers.
		// The panel is a child of the item's `<li>`, so moving from the trigger into the panel never fires a leave.
		root.addEventListener('pointerenter', () => {
			if (hoverCapable.matches) {
				setExpanded(trigger, panel, true);
			}
		});
		root.addEventListener('pointerleave', () => {
			if (hoverCapable.matches) {
				setExpanded(trigger, panel, false);
			}
		});

		// 3. Escape closes and returns focus to the trigger.
		root.addEventListener('keydown', (event: KeyboardEvent) => {
			if (event.key === 'Escape' && isOpen()) {
				setExpanded(trigger, panel, false);
				trigger.focus();
			}
		});

		// 4. Tabbing out of the item closes it, so an opened panel never lingers once keyboard users move on.
		root.addEventListener('focusout', (event: FocusEvent) => {
			const next = event.relatedTarget;
			if (isOpen() && (!(next instanceof Node) || !root.contains(next))) {
				setExpanded(trigger, panel, false);
			}
		});

		// 5. Pressing anywhere outside the item closes it.
		document.addEventListener('pointerdown', (event: PointerEvent) => {
			if (isOpen() && event.target instanceof Node && !root.contains(event.target)) {
				setExpanded(trigger, panel, false);
			}
		});
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initNavDisclosures);
} else {
	initNavDisclosures();
}
